package at.theatervogelweide.admin.web.screens.acts

import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.ImageDetails
import at.theatervogelweide.admin.contract.acts.ActDraft
import at.theatervogelweide.admin.contract.acts.ActListEntry
import at.theatervogelweide.admin.contract.acts.CastRole
import at.theatervogelweide.admin.contract.acts.Performance
import at.theatervogelweide.admin.contract.moved
import at.theatervogelweide.admin.web.components.dateError
import at.theatervogelweide.admin.web.components.parseDateField
import at.theatervogelweide.admin.web.components.parseTimeField
import at.theatervogelweide.admin.web.components.timeError
import at.theatervogelweide.domain.TheaterTime
import at.theatervogelweide.domain.TicketAnnouncement
import at.theatervogelweide.domain.TicketSchedule
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.daysUntil

/**
 * A performance as typed, in theater time. [key] stays with the row while others are added and removed; [id] is the
 * saved performance's.
 */
data class PerformanceRow(
    val key: Int,
    val dateText: String = "",
    val timeText: String = "",
    val soldOut: Boolean = false,
    val id: Long? = null,
)

/** A role as edited, in either group; [id] is the saved role's. */
data class CastRow(
    val key: Int,
    val id: Long? = null,
    val name: String = "",
    val personIds: List<Long> = emptyList(),
)

/** The two groups of the cast (#104). */
enum class CastGroup(val title: String) {
    CAST("Besetzung"),
    CREW("Hinter der Bühne"),
}

/** The editor's fields as typed; dates, times and the year stay text until saving. Images are kept apart. */
data class ActForm(
    val title: String = "",
    val yearText: String = "",
    val description: String = "",
    val shortText: String = "",
    val performances: List<PerformanceRow> = emptyList(),
    val saleDateText: String = "",
    val saleTimeText: String = "",
    val reservationUrl: String = "",
    val venueName: String = "",
    val venueAddress: String = "",
    val admission: String = "",
    val cast: List<CastRow> = emptyList(),
    val crew: List<CastRow> = emptyList(),
    /** The key the next new row gets. */
    val nextKey: Int = 0,
) {
    fun addPerformance(): ActForm =
        // most performances start at the same time as the one before
        copy(performances = performances + PerformanceRow(nextKey, timeText = performances.lastOrNull()?.timeText.orEmpty()), nextKey = nextKey + 1)

    fun removePerformance(key: Int): ActForm = copy(performances = performances.filterNot { it.key == key })

    fun updatePerformance(key: Int, change: PerformanceRow.() -> PerformanceRow): ActForm =
        copy(performances = performances.map { if (it.key == key) it.change() else it })

    fun addRole(group: CastGroup): ActForm = when (group) {
        CastGroup.CAST -> copy(cast = cast + CastRow(nextKey), nextKey = nextKey + 1)
        CastGroup.CREW -> copy(crew = crew + CastRow(nextKey), nextKey = nextKey + 1)
    }

    fun rolesOf(group: CastGroup): List<CastRow> = when (group) {
        CastGroup.CAST -> cast
        CastGroup.CREW -> crew
    }

    fun groupOf(key: Int): CastGroup? = when {
        cast.any { it.key == key } -> CastGroup.CAST
        crew.any { it.key == key } -> CastGroup.CREW
        else -> null
    }

    fun removeCastRow(key: Int): ActForm = copy(cast = cast.filterNot { it.key == key }, crew = crew.filterNot { it.key == key })

    fun updateCastRow(key: Int, change: CastRow.() -> CastRow): ActForm =
        copy(cast = cast.map { if (it.key == key) it.change() else it }, crew = crew.map { if (it.key == key) it.change() else it })

    /**
     * Both groups as one list for reordering: the cast's keys, [GROUP_BOUNDARY], the crew's keys. Moving a role past
     * the boundary moves it into the other group, so Alt+↓ on the cast's last role makes it the crew's first.
     */
    val castOrder: List<Int> get() = cast.map { it.key } + GROUP_BOUNDARY + crew.map { it.key }

    /** Moves within [castOrder], indices as there. */
    fun moveRole(from: Int, to: Int): ActForm {
        val rows = (cast + crew).associateBy { it.key }
        val order = castOrder.moved(from, to)
        val boundary = order.indexOf(GROUP_BOUNDARY)
        return copy(
            cast = order.subList(0, boundary).map(rows::getValue),
            crew = order.subList(boundary + 1, order.size).map(rows::getValue),
        )
    }

    /** The key of the earliest performance that can be read, which the public site shows as the premiere. */
    val premiereKey: Int?
        get() = performances.mapNotNull { row -> row.startsAt()?.let { row.key to it } }.minByOrNull { it.second }?.first

    /** When the ticket page shows the act, from the fields that can be read so far. */
    val ticketSchedule: TicketSchedule?
        get() = TicketAnnouncement.schedule(saleStart(), performances.mapNotNull { it.startsAt() })

    companion object {
        fun of(draft: ActDraft): ActForm {
            val performances = draft.performances.sortedBy { it.startsAt }.mapIndexed { index, performance ->
                val local = TheaterTime.fromUtc(performance.startsAt)
                PerformanceRow(index, TheaterTime.format(local.date), TheaterTime.format(local.time), performance.soldOut, performance.id)
            }
            val saleStart = draft.ticketSaleStart?.let(TheaterTime::fromUtc)
            val cast = draft.cast.mapIndexed { index, role -> CastRow(performances.size + index, role.id, role.name, role.personIds) }
            val crew = draft.crew.mapIndexed { index, role ->
                CastRow(performances.size + cast.size + index, role.id, role.name, role.personIds)
            }

            return ActForm(
                title = draft.title,
                yearText = if (draft.year == 0) "" else draft.year.toString(),
                description = draft.description,
                shortText = draft.shortText,
                performances = performances,
                saleDateText = saleStart?.let { TheaterTime.format(it.date) }.orEmpty(),
                saleTimeText = saleStart?.let { TheaterTime.format(it.time) }.orEmpty(),
                reservationUrl = draft.reservationUrl,
                venueName = draft.venueName,
                venueAddress = draft.venueAddress,
                admission = draft.admission,
                cast = cast,
                crew = crew,
                nextKey = performances.size + cast.size + crew.size,
            )
        }

        /** The field of a performance's date or time, e.g. `performance.3.date`; web only, the server sees no rows. */
        fun performanceDateField(key: Int) = "performance.$key.date"

        fun performanceTimeField(key: Int) = "performance.$key.time"

        /** The place of the group boundary in [castOrder]; row keys are never negative. */
        const val GROUP_BOUNDARY = -1
    }
}

private fun PerformanceRow.startsAt(): LocalDateTime? {
    val date = parseDateField(dateText) ?: return null
    val time = parseTimeField(timeText) ?: return null
    return TheaterTime.toUtc(LocalDateTime(date, time))
}

private fun ActForm.saleStart(): LocalDateTime? {
    val date = parseDateField(saleDateText) ?: return null
    val time = parseTimeField(saleTimeText) ?: return null
    return TheaterTime.toUtc(LocalDateTime(date, time))
}

/**
 * The draft to save with the images, or the field errors that keep it from being saved. Rows with neither date nor
 * time are left out, like the old editor did. The roles' field errors name the row's group and position, e.g. `cast.2`
 * or `crew.0`.
 */
fun ActForm.toDraft(
    mainImageUuid: String?,
    imageUuids: List<String>,
    imageDetails: Map<String, ImageDetails> = emptyMap(),
): Pair<ActDraft?, List<FieldError>> {
    val year = yearText.trim().toIntOrNull()
    val formErrors = buildList {
        if (year == null) {
            add(FieldError(ActDraft.YEAR, "Bitte ein Jahr wie 2026 eingeben."))
        }
        performances.filterNot { it.dateText.isBlank() && it.timeText.isBlank() }.forEach { row ->
            dateError(row.dateText, optional = false)?.let { add(FieldError(ActForm.performanceDateField(row.key), it)) }
            timeError(row.timeText, optional = false)?.let { add(FieldError(ActForm.performanceTimeField(row.key), it)) }
        }
        when {
            saleDateText.isBlank() && saleTimeText.isNotBlank() ->
                add(FieldError(ActDraft.TICKET_SALE_START, "Bitte auch ein Datum eingeben oder die Uhrzeit leeren."))

            else -> {
                dateError(saleDateText, optional = true)?.let { add(FieldError(ActDraft.TICKET_SALE_START, it)) }
                if (saleDateText.isNotBlank()) {
                    timeError(saleTimeText, optional = false)?.let { add(FieldError(SALE_TIME, it)) }
                }
            }
        }
    }

    val draft = ActDraft(
        title = title.trim(),
        year = year ?: 0,
        description = description,
        shortText = shortText.trim(),
        performances = performances.mapNotNull { row -> row.startsAt()?.let { Performance(it, row.soldOut, row.id) } },
        ticketSaleStart = saleStart(),
        reservationUrl = reservationUrl.trim(),
        venueName = venueName.trim(),
        venueAddress = venueAddress.trim(),
        admission = admission.trim(),
        cast = cast.map { CastRole(it.id, it.name.trim(), it.personIds) },
        crew = crew.map { CastRole(it.id, it.name.trim(), it.personIds) },
        mainImageUuid = mainImageUuid,
        imageUuids = imageUuids,
        imageDetails = imageDetails,
    )
    val errors = formErrors + draft.validate().filter { error -> formErrors.none { it.field == error.field } }

    return if (errors.isEmpty()) draft to emptyList() else null to errors
}

/** The time field of the ticket sale start, whose errors the server never sends. */
const val SALE_TIME = "ticketSaleTime"

/** `Fr 07.11.2026, 19:30` in theater time. */
fun formatDateTime(utc: LocalDateTime): String {
    val local = TheaterTime.fromUtc(utc)
    return "${local.date.dayOfWeek.shortName} ${TheaterTime.format(local.date)}, ${TheaterTime.format(local.time)}"
}

/** `07.11.2026` in theater time. */
fun formatDate(utc: LocalDateTime): String = TheaterTime.format(TheaterTime.fromUtc(utc).date)

private val DayOfWeek.shortName: String
    get() = when (this) {
        DayOfWeek.MONDAY -> "Mo"
        DayOfWeek.TUESDAY -> "Di"
        DayOfWeek.WEDNESDAY -> "Mi"
        DayOfWeek.THURSDAY -> "Do"
        DayOfWeek.FRIDAY -> "Fr"
        DayOfWeek.SATURDAY -> "Sa"
        DayOfWeek.SUNDAY -> "So"
    }

/** "erledigt", "heute", "morgen" or "in 16 Tagen", counted in theater days. */
fun relativeState(at: LocalDateTime, now: LocalDateTime): String {
    if (at <= now) {
        return "erledigt"
    }
    return when (val days = TheaterTime.fromUtc(now).date.daysUntil(TheaterTime.fromUtc(at).date)) {
        0 -> "heute"
        1 -> "morgen"
        else -> "in $days Tagen"
    }
}

/** The list's meta line: "6 Vorstellungen · Premiere 07.11.2026" or "Keine Vorstellungen". */
fun ActListEntry.metaText(): String = when (performances.size) {
    0 -> "Keine Vorstellungen"
    1 -> "1 Vorstellung am ${formatDate(performances.first())}"
    else -> "${performances.size} Vorstellungen · Premiere ${formatDate(performances.first())}"
}
